<template>
  <div class="px-8">
    <ol
      class="max-h-112 mb-8 flex flex-col-reverse space-y-4 overflow-auto pr-4"
    >
      <template v-for="message in messages" :key="message.id">
        <li>
          <div
            :class="
              message.direction === 'incoming'
                ? 'pr-20 text-left'
                : 'pl-20 text-right'
            "
          >
            <p :class="['text-primary mb-2 font-medium']">
              <span
                v-if="message.isCompanion"
                class="bg-orange text-orange mr-2 inline-flex rounded-full bg-opacity-20 px-2 text-xs font-normal"
              >
                Companion
              </span>
              <span
                v-if="!message.sent && !message.failed"
                class="bg-background-fg/7 mr-2 inline-flex rounded-full px-2 text-xs font-normal text-black/70"
              >
                pending
              </span>
              <span
                v-if="message.failed"
                class="bg-red text-red mr-2 inline-flex rounded-full bg-opacity-20 px-2 text-xs font-normal"
              >
                failed
              </span>
              <svg
                v-if="message.delivered && message.sent && !message.failed"
                class="mr-2 inline-flex"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="8" cy="8" r="8" fill="#10B981" />
                <path
                  d="M11.5 5.5L6.75 10.25L4.5 8"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {{ formatShortDateTime(message.createdAt) }}
            </p>
            <p :class="message.failed && 'text-black/40'">
              {{ message.content }}
            </p>
          </div>
        </li>
      </template>
    </ol>
    <section>
      <BaseForm class="relative space-y-4" @submit="onSendSMSSubmit">
        <BaseInputTextarea
          v-model="customSMSContent"
          :rules="customSMSContentRules"
          :hint="customSMSContentHint"
          name="customSMSContent"
        />
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <button
            class="ring-secondary text-secondary hover:bg-secondary/10 rounded px-2 py-1 ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-inherit sm:col-start-4"
            type="submit"
          >
            Send SMS
          </button>
        </div>

        <template v-if="isCustomSMSSending">
          <BaseContainerLoading />
        </template>
      </BaseForm>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ApiError } from "@/api";

const props = defineProps<{
  bookingId: string;
  isCompanion?: boolean;
}>();

const { data: messages, refetch: refetchMessages } = useBookingMessages(
  toRef(props, "bookingId"),
);

const formatShortDateTime = (date: Date) => {
  return DateFns.format(date, "dd MMM, HH:mm");
};

const customSMSContent = ref("");
const isCustomSMSSending = ref(false);
// SMS Works rejects a body over 1280 characters. Companion bookings only get a
// notification text, with the message itself read in the portal, so no limit.
const SMS_MAX_LENGTH = 1280;
const customSMSContentLength = computed(
  () => customSMSContent.value.trim().length,
);
const customSMSContentHint = computed(() =>
  props.isCompanion
    ? undefined
    : `${customSMSContentLength.value} / ${SMS_MAX_LENGTH} characters`,
);
const customSMSContentRules = [
  (value: string) => !!value.trim() || "Please enter sms message content",
  (value: string) =>
    props.isCompanion ||
    value.trim().length <= SMS_MAX_LENGTH ||
    `Too long to send as a text: ${value.trim().length} of ${SMS_MAX_LENGTH} characters`,
];
const onSendSMSSubmit = async () => {
  isCustomSMSSending.value = true;
  try {
    await sendBookingSms(props.bookingId, customSMSContent.value);

    toast.success("Sent SMS");
    customSMSContent.value = "";
  } catch (error) {
    // A 400 carries the reason (too long, no phone number); show it.
    toast.error(
      error instanceof ApiError && error.status === 400 && error.detail
        ? `Failed to send SMS: ${error.detail}`
        : "Failed to send SMS",
    );
  } finally {
    isCustomSMSSending.value = false;
    refetchMessages();
  }
};
</script>
